import { Injectable } from '@angular/core';
import { setDoc } from 'firebase/firestore';
import { convertToDate } from '@shared/helpers/convert-to-date';
import { firestoreDeepClone } from '@shared/helpers/firestore-deep-clone';
import { ReplicationSyncStatus } from '@shared/models/replication-sync-status';
import { SalesRefund } from 'src/app/point-of-sale/models/sales-refund-v3';
import { BaseReplicableService } from './base-replicable.service';

/**
 * Persists POS sales refunds (Order 3.0) to Firestore, which is the source of
 * truth the cash-register closure reads its refund figures from.
 *
 * Refunds share the `salesOrdersV3` collection with sales orders, exactly as
 * they share the Desktop's `SalesOrdersV3` store: both are POS *sales
 * transactions* for the same register, and every reader already tells them
 * apart by their `REF`/`SO` code prefix. Keeping one collection also means the
 * closure's register+date queries keep using the indexes and Firestore rules
 * that collection already has.
 *
 * Replication is deliberately NOT enabled: a refund is created online-first —
 * the backend accepts it before this service ever sees it — so there is nothing
 * for the sync pipeline to push and no reason to mirror it to the Desktop.
 * Every refund is therefore stored already `Synced`, which is also what keeps
 * it out of the `UnSynced`/`Syncing`/`Failed` queries that drive the backend
 * push (main thread and service worker alike) and the sales-orders grid.
 *
 * @see US 27613 — Save and Calculate Refunds from Firestore
 */
@Injectable({
  providedIn: 'root'
})
export class SalesRefundReplicationService extends BaseReplicableService<SalesRefund> {
  protected readonly entityType = 'salesRefund';
  protected readonly endpoint = 'SalesOrdersV3';
  protected readonly firestoreCollection = 'salesOrdersV3';

  /**
   * Store a refund the backend has already accepted.
   *
   * `orderDate` and `paymentDate` are the fields the closure filters its shift
   * window on, so they must reach Firestore as real Dates (Timestamps) rather
   * than strings — see {@link normalizeDates}.
   */
  async createRefund(refund: SalesRefund): Promise<SalesRefund> {
    refund.orderDate = refund.orderDate
      ? convertToDate(refund.orderDate)
      : this.firestoreService.serverDateTime();

    this.materializeTotals(refund);
    this.ensureValidMetadata(refund);
    refund.metadata!.syncInfo = {
      ...(refund.metadata!.syncInfo ?? {}),
      syncStatus: ReplicationSyncStatus.Synced,
      syncedDate: new Date(),
      syncedBy: this.getSanitizedUserProfile()
    };

    return this.create(refund);
  }

  /**
   * Bring `grossTotal` and `netTotal` up to date before the refund is stored.
   *
   * Both are plain fields seeded from the *originating sales order* by
   * {@link SalesRefund.cloneFromSalesOrder} and only later overwritten, as a
   * side effect, by the `grossAmount`/`netAmount` getters the refund form
   * renders. The getters live on the prototype, so neither
   * `firestoreDeepClone` nor `JSON.stringify` carries them into the stored
   * document — and the closure reads the two fields as stored values rather
   * than recomputing them. Reading the getters here makes the stored totals the
   * refund's own, whatever the form happened to render.
   */
  private materializeTotals(refund: SalesRefund): void {
    // Assigned rather than merely read, so a minifier can never drop what looks
    // like a side-effect-free property access. Order matters: `netAmount` is
    // derived from `grossAmount`.
    refund.grossTotal = refund.grossAmount;
    refund.netTotal = refund.netAmount;
  }

  /**
   * Refunds carry Date fields the closure filters with range queries, so they
   * must be stored as Timestamps. `firestoreDeepClone` preserves `Date`
   * instances, unlike the base implementation's JSON round-trip which would
   * turn them into strings.
   */
  protected override async saveToFirestore(refund: SalesRefund): Promise<void> {
    const documentRef = this.firestoreService.doc(
      `${this.firestoreCollection}/${this.getDocumentId(refund)}`
    );
    this.normalizeDates(refund);
    const plainObject = firestoreDeepClone(refund);
    await this.firestoreService.settleWrite(
      () => setDoc(documentRef, plainObject),
      documentRef.path
    );
  }

  /**
   * Turn every date the refund carries into a real `Date`.
   *
   * Two reasons, not one: `orderDate`/`paymentDate` are queried with range
   * filters and so have to be Timestamps, and `firestoreDeepClone` walks values
   * with `for...in` — which would flatten a raw Firestore `Timestamp` into a
   * plain map. The register's opening date and the originating order's
   * fulfillment date both arrive as ISO strings from the Desktop and the
   * backend respectively, so neither can be assumed to be a Date already.
   * Unparseable values are left untouched rather than turned into `Invalid Date`.
   */
  private normalizeDates(refund: SalesRefund): void {
    refund.orderDate = this.toValidDate(refund.orderDate) ?? refund.orderDate;
    refund.paymentDate = this.toValidDate(refund.paymentDate) ?? refund.paymentDate;

    if (refund.extraAttributes?.businessDay) {
      refund.extraAttributes.businessDay =
        this.toValidDate(refund.extraAttributes.businessDay) ?? refund.extraAttributes.businessDay;
    }
    if (refund.extraAttributes?.salesOrderFulfillmentDate) {
      refund.extraAttributes.salesOrderFulfillmentDate =
        this.toValidDate(refund.extraAttributes.salesOrderFulfillmentDate)
        ?? refund.extraAttributes.salesOrderFulfillmentDate;
    }
    if (refund.metadata?.createdDate) {
      refund.metadata.createdDate = this.toValidDate(refund.metadata.createdDate) ?? refund.metadata.createdDate;
    }
    if (refund.metadata?.syncInfo?.syncedDate) {
      refund.metadata.syncInfo.syncedDate =
        this.toValidDate(refund.metadata.syncInfo.syncedDate) ?? refund.metadata.syncInfo.syncedDate;
    }
  }

  /** A valid `Date` for Timestamp/ISO-string/Date input, otherwise undefined. */
  private toValidDate(value: unknown): Date | undefined {
    if (!value) return undefined;
    const date = convertToDate(value as any);
    return date instanceof Date && !isNaN(date.getTime()) ? date : undefined;
  }
}
